import { getInitials } from '../../utils/helpers';

interface AvatarProps {
  name: string;
  color: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs';
  isOnline?: boolean;
  className?: string;
}

const sizes = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-12 h-12 text-base',
};

export default function Avatar({ name, color, size = 'md', isOnline, className = '' }: AvatarProps) {
  return (
    <div className={`relative inline-flex ${className}`}>
      <div
        className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold text-white shrink-0 ring-2 ring-white`}
        style={{ backgroundColor: color }}
        title={name}
        role="img"
        aria-label={name}
      >
        {getInitials(name)}
      </div>
      {isOnline !== undefined && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 block rounded-full ring-2 ring-white ${
            isOnline ? 'bg-emerald-400 animate-pulse-dot' : 'bg-slate-300'
          } ${size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5'}`}
          aria-label={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
}

interface AvatarGroupProps {
  users: { name: string; color: string }[];
  max?: number;
  size?: 'sm' | 'md' | 'lg';
}

export function AvatarGroup({ users, max = 3, size = 'sm' }: AvatarGroupProps) {
  const visible = users.slice(0, max);
  const remaining = users.length - max;

  return (
    <div className="flex -space-x-1.5" role="group" aria-label="Team members">
      {visible.map((user, i) => (
        <Avatar key={i} name={user.name} color={user.color} size={size} />
      ))}
      {remaining > 0 && (
        <div
          className={`${sizes[size]} rounded-full flex items-center justify-center font-medium text-slate-600 bg-slate-200 ring-2 ring-white shrink-0`}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}
